import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Edit, Trash2, Briefcase, Sparkles, Users } from 'lucide-react';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import Textarea from '@/components/common/Textarea';
import Modal from '@/components/common/Modal';
import { toast } from '@/stores/toastStore';
import recruiterJobService from '@/services/recruiterJobService';
import {
  RecruiterJob,
  CreateRecruiterJobRequest,
  EmploymentType,
  WorkMode,
  RecruiterJobStatus,
} from '@/types';

const employmentTypeOptions = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERNSHIP', label: 'Internship' },
];

const workModeOptions = [
  { value: 'REMOTE', label: 'Remote' },
  { value: 'HYBRID', label: 'Hybrid' },
  { value: 'ONSITE', label: 'Onsite' },
];

const currencyOptions = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'TND', label: 'TND (د.ت)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
];

const statusOptions = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' },
];

interface JobFormData {
  title: string;
  description: string;
  position: string;
  location: string;
  employmentType: EmploymentType;
  workMode: WorkMode;
  salaryMin: number | undefined;
  salaryMax: number | undefined;
  salaryCurrency: string;
  benefits: string;
  responsibilities: string;
  requirements: string;
  status: RecruiterJobStatus;
  defaultExpiryDays: number | undefined;
}

const defaultFormData: JobFormData = {
  title: '',
  description: '',
  position: '',
  location: '',
  employmentType: 'FULL_TIME',
  workMode: 'HYBRID',
  salaryMin: undefined,
  salaryMax: undefined,
  salaryCurrency: 'TND',
  benefits: '',
  responsibilities: '',
  requirements: '',
  status: 'ACTIVE',
  defaultExpiryDays: 14,
};

const MIN_DESCRIPTION_LENGTH = 50

export default function JobsTab() {
  const { t } = useTranslation('jobOffers');
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<RecruiterJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingJob, setDeletingJob] = useState<RecruiterJob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<JobFormData>(defaultFormData);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await recruiterJobService.listJobs({ limit: 100 });
      setJobs(response.jobs);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || t('jobs.errors.loadFailed', 'Failed to load jobs'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async () => {
    if (!formData.title || !formData.description) {
      toast.error(t('jobs.form.titleRequired', 'Title and description are required'));
      return;
    }
    if (formData.description.trim().length < MIN_DESCRIPTION_LENGTH) {
      toast.error(t('jobs.form.descriptionTooShort', 'Description must be at least 50 characters'));
      return;
    }
    if (formData.defaultExpiryDays !== undefined && (formData.defaultExpiryDays < 1 || formData.defaultExpiryDays > 365)) {
      toast.error(t('templates.form.expiryDaysInvalid'));
      return;
    }
    try {
      setSubmitting(true);
      const submitData: CreateRecruiterJobRequest = {
        title: formData.title,
        description: formData.description,
        position: formData.position || undefined,
        location: formData.location || undefined,
        employmentType: formData.employmentType,
        workMode: formData.workMode,
        salaryMin: formData.salaryMin,
        salaryMax: formData.salaryMax,
        salaryCurrency: formData.salaryCurrency,
        benefits: formData.benefits || undefined,
        responsibilities: formData.responsibilities || undefined,
        requirements: formData.requirements || undefined,
        status: formData.status,
        defaultExpiryDays: formData.defaultExpiryDays,
      };
      const created = await recruiterJobService.createJob(submitData);
      toast.success(t('jobs.success.created', 'Job created successfully'));
      setShowCreateModal(false);
      setFormData(defaultFormData);
      navigate(`/recruiter/jobs/${created.id}`);
    } catch (error: unknown) {
      const rawError = (error as { response?: { data?: { error?: unknown } } }).response?.data?.error;
      let errorMessage: string;
      if (typeof rawError === 'string') {
        errorMessage = rawError;
      } else if (rawError && typeof rawError === 'object') {
        const zodErr = rawError as { issues?: Array<{ message: string }> };
        errorMessage = zodErr.issues?.[0]?.message ?? t('jobs.errors.createFailed', 'Failed to create job');
      } else {
        errorMessage = t('jobs.errors.createFailed', 'Failed to create job');
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!deletingJob) return;
    try {
      setSubmitting(true);
      await recruiterJobService.deleteJob(deletingJob.id);
      toast.success(t('jobs.success.deleted', 'Job deleted successfully'));
      setDeletingJob(null);
      fetchJobs();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || t('jobs.errors.deleteFailed', 'Failed to delete job'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof JobFormData, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filteredJobs = jobs.filter(job => {
    const query = searchQuery.toLowerCase();
    return (
      job.title.toLowerCase().includes(query) ||
      (job.position || '').toLowerCase().includes(query) ||
      job.description.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  const statusColors: Record<RecruiterJobStatus, string> = {
    ACTIVE: 'bg-green-100 text-green-700',
    DRAFT: 'bg-yellow-100 text-yellow-700',
    ARCHIVED: 'bg-gray-100 text-gray-600',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Search and Create Button */}
      {jobs.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              type="text"
              placeholder={t('jobs.list.searchPlaceholder', 'Search jobs...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="w-5 h-5" />}
            />
          </div>
          <Button variant="primary" onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {t('jobs.list.createButton', 'Create Job')}
          </Button>
        </div>
      )}

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <Card className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {jobs.length === 0
              ? t('jobs.list.empty.title', 'No jobs yet')
              : t('jobs.list.empty.noResults', 'No matching jobs')}
          </h2>
          <p className="text-gray-600 mb-6">
            {jobs.length === 0
              ? t('jobs.list.empty.description', 'Create your first job listing')
              : t('jobs.list.empty.noResultsHint', 'Try adjusting your search query')}
          </p>
          {jobs.length === 0 && (
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              {t('jobs.list.empty.action', 'Create Job')}
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredJobs.map((job) => (
            <Card
              key={job.id}
              hover
              className="hover:shadow-xl transition-shadow"
              onClick={() => navigate(`/recruiter/jobs/${job.id}`)}
            >
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                    {job.embeddingProcessedAt !== null && (
                      <span className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                        <Sparkles className="w-3 h-3" />
                        AI
                      </span>
                    )}
                  </div>
                  {job.position && <p className="text-sm text-gray-600">{job.position}</p>}
                </div>

                <div className="space-y-2 mb-4 flex-grow">
                  <div className="flex gap-2 flex-wrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[job.status]}`}>
                      {job.status}
                    </span>
                    {job.employmentType && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {t(`employmentTypes.${job.employmentType}`)}
                      </span>
                    )}
                    {job.workMode && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {t(`workModes.${job.workMode}`)}
                      </span>
                    )}
                  </div>
                  {job.extractedSkills && job.extractedSkills.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {job.extractedSkills.slice(0, 3).map((skill) => (
                        <span key={skill} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                          {skill}
                        </span>
                      ))}
                      {job.extractedSkills.length > 3 && (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                          +{job.extractedSkills.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    {t('templates.list.card.lastUpdated')}: {formatDate(job.updatedAt)}
                  </p>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); navigate(`/recruiter/jobs/${job.id}`); }}
                    className="flex-1 flex items-center justify-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    {t('templates.list.card.edit')}
                  </Button>
                  {(job.prospectsCount ?? 0) > 0 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); navigate(`/recruiter/jobs/${job.id}/prospects`); }}
                      className="flex items-center justify-center gap-1"
                      title="View Prospects"
                    >
                      <Users className="w-4 h-4" />
                      {job.prospectsCount}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/recruiter/ai-matching/create?jobId=${job.id}`);
                    }}
                    className="flex items-center justify-center gap-1"
                    title="Run AI Match"
                  >
                    <Sparkles className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setDeletingJob(job); }}
                    className="flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal
          isOpen={true}
          onClose={() => { setShowCreateModal(false); setFormData(defaultFormData); }}
          title={t('jobs.create.title', 'Create Job')}
          size="xl"
        >
          <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
            <p className="text-gray-600">{t('jobs.create.subtitle', 'Create a new job listing')}</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('jobs.form.titleLabel', 'Job Title')} <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={t('jobs.form.titlePlaceholder', 'e.g., Senior Software Engineer')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('jobs.form.descriptionLabel', 'Description')} <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('jobs.form.descriptionPlaceholder', 'Full job description (min. 50 characters)...')}
                rows={4}
              />
              <p className={`mt-1 text-xs ${formData.description.trim().length < MIN_DESCRIPTION_LENGTH ? 'text-amber-600' : 'text-gray-400'}`}>
                {formData.description.trim().length < MIN_DESCRIPTION_LENGTH
                  ? `${formData.description.trim().length} / ${MIN_DESCRIPTION_LENGTH} characters minimum`
                  : `${formData.description.trim().length} characters`}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.positionLabel')}
              </label>
              <Input
                type="text"
                value={formData.position}
                onChange={(e) => handleInputChange('position', e.target.value)}
                placeholder={t('templates.form.positionPlaceholder')}
                icon={<Briefcase className="w-5 h-5" />}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.locationLabel')}
              </label>
              <Input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder={t('templates.form.locationPlaceholder')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label={t('templates.form.employmentTypeLabel')}
                value={formData.employmentType}
                onChange={(e) => handleInputChange('employmentType', e.target.value as EmploymentType)}
                options={employmentTypeOptions}
              />
              <Select
                label={t('templates.form.workModeLabel')}
                value={formData.workMode}
                onChange={(e) => handleInputChange('workMode', e.target.value as WorkMode)}
                options={workModeOptions}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.salaryMinLabel')}
                </label>
                <Input
                  type="number"
                  value={formData.salaryMin || ''}
                  onChange={(e) => handleInputChange('salaryMin', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder={t('templates.form.salaryMinPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.salaryMaxLabel')}
                </label>
                <Input
                  type="number"
                  value={formData.salaryMax || ''}
                  onChange={(e) => handleInputChange('salaryMax', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder={t('templates.form.salaryMaxPlaceholder')}
                />
              </div>
              <Select
                label={t('templates.form.currencyLabel')}
                value={formData.salaryCurrency}
                onChange={(e) => handleInputChange('salaryCurrency', e.target.value)}
                options={currencyOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.responsibilitiesLabel')}
              </label>
              <Textarea
                value={formData.responsibilities}
                onChange={(e) => handleInputChange('responsibilities', e.target.value)}
                placeholder={t('templates.form.responsibilitiesPlaceholder')}
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.requirementsLabel')}
              </label>
              <Textarea
                value={formData.requirements}
                onChange={(e) => handleInputChange('requirements', e.target.value)}
                placeholder={t('templates.form.requirementsPlaceholder')}
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.benefitsLabel')}
              </label>
              <Textarea
                value={formData.benefits}
                onChange={(e) => handleInputChange('benefits', e.target.value)}
                placeholder={t('templates.form.benefitsPlaceholder')}
                rows={3}
              />
            </div>

            <Select
              label={t('jobs.form.statusLabel', 'Status')}
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as RecruiterJobStatus)}
              options={statusOptions}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('templates.form.expiryDaysLabel', 'Default Expiry Days')}
              </label>
              <Input
                type="number"
                min="1"
                max="365"
                value={formData.defaultExpiryDays?.toString() || ''}
                onChange={(e) => handleInputChange('defaultExpiryDays', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder={t('templates.form.expiryDaysPlaceholder')}
              />
              <p className="text-sm text-gray-500 mt-1">{t('templates.form.expiryDaysHint')}</p>
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button
                variant="primary"
                onClick={handleCreateJob}
                disabled={submitting}
                className="flex-1"
              >
                {submitting ? t('templates.form.savingButton') : t('jobs.form.saveButton', 'Save Job')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => { setShowCreateModal(false); setFormData(defaultFormData); }}
                disabled={submitting}
                className="flex-1"
              >
                {t('templates.form.cancelButton')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deletingJob && (
        <Modal
          isOpen={true}
          onClose={() => setDeletingJob(null)}
          title={t('jobs.delete.title', 'Delete Job')}
        >
          <div className="p-6">
            <p className="text-gray-700 mb-6">
              {t('jobs.delete.message', 'Are you sure you want to delete this job? This action cannot be undone.')}
            </p>
            <div className="flex gap-3">
              <Button variant="danger" onClick={handleDeleteJob} disabled={submitting} className="flex-1">
                {t('templates.delete.confirm')}
              </Button>
              <Button variant="secondary" onClick={() => setDeletingJob(null)} disabled={submitting} className="flex-1">
                {t('templates.delete.cancel')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
